import { showUserAlert, userErrorMessage } from "../utils/userErrorMessage";
import React, { useEffect, useRef, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { supabase } from "../supabase/client";
import Header from "../Component/Header";
import usePersistedUI from "../hooks/usePersistedUI"; // ✅ 추가
import { buildScheduleSmsMessage, buildUserSmsMessage } from "../utils/scheduleSmsMessage";

const getToday = () => {
  const today = new Date();
  return today.toISOString().split("T")[0]; // 'YYYY-MM-DD'
};

const getTomorrow = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split("T")[0];
};

const isSolapiChargeError = (message = "") => {
  const text = String(message || "").toLowerCase();
  return ["충전", "잔액", "포인트", "캐시", "balance", "point", "cash", "insufficient"].some((keyword) =>
    text.includes(keyword)
  );
};

const isSolapiSenderNumberError = (message = "") => {
  const text = String(message || "").toLowerCase();
  return [
    "발신번호",
    "발신 번호",
    "발송번호",
    "발송 번호",
    "not registered",
    "not verified",
    "unverified",
    "unauthorized sender",
    "sender number",
    "registered sender",
    "unregistered sender",
  ].some((keyword) => text.includes(keyword));
};

const getUserFriendlySmsError = (message = "") => {
  if (isSolapiChargeError(message)) return "Solapi에서 충전을 부탁드립니다.";
  if (isSolapiSenderNumberError(message)) return "Solapi에 가입되어있지 않은 번호입니다.";
  return "";
};

const readFunctionErrorMessage = async (error) => {
  const response = await error?.context?.clone?.().json().catch(() => null);
  return response?.error || error?.message || "";
};

const getTimeSortValue = (value) => {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return Number(match[1]) * 60 + Number(match[2]);
};

async function resolveCurrentCompanyForSms() {
  const cachedCompany = String(localStorage.getItem("email") || "").trim();
  const cachedCompanyName = String(localStorage.getItem("companyName") || "").trim();
  const cachedUserEmail = String(localStorage.getItem("userEmail") || "").trim();

  const { data: sessionData } = await supabase.auth.getSession();
  const userEmail = String(sessionData?.session?.user?.email || cachedUserEmail || "").trim();

  if (!userEmail) {
    return {
      company: cachedCompany,
      companyName: cachedCompanyName,
    };
  }

  const { data: profile, error } = await supabase
    .from("profiles")
    .select("company, company_name")
    .eq("email", userEmail)
    .maybeSingle();

  if (error) {
    console.error("SMS 회사 정보 조회 실패:", error);
    return {
      company: cachedCompany,
      companyName: cachedCompanyName,
    };
  }

  const company = String(profile?.company || cachedCompany || "").trim();
  const companyName = String(profile?.company_name || cachedCompanyName || profile?.company || "").trim();

  if (company) localStorage.setItem("email", company);
  if (companyName) localStorage.setItem("companyName", companyName);

  return { company, companyName };
}

export default function ConfirmedSchedule() {
  // 가람
  // ❌ 기존 useState 삭제
  // const [filters, setFilters] = useState({ 사용자: "", 출발지: "", 행선지: "", 차량: "", 기사명: "" });
  // const [date, setDate] = useState(getToday());
  const [data, setData] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [extraMessages, setExtraMessages] = useState({});
  const [extraMessageModal, setExtraMessageModal] = useState(null);
  const [extraMessageSaving, setExtraMessageSaving] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const fetchRequestIdRef = useRef(0);

  // ✅ Reservation.jsx와 같은 방식으로 공용 훅 사용
  const ui = usePersistedUI(
    "confirmed-schedule", // 이 화면만의 고유 키
    {
      dateRange: { from: getTomorrow(), to: getTomorrow() }, // 여기선 from만 사용
      filters: { 사용자: "", 출발지: "", 행선지: "", 차량: "", 기사: "" },
    },
    { urlSync: true } // URL 쿼리 동기화 (선택)
  );

  const { state } = ui;

  const fetchData = async () => {
    const requestId = ++fetchRequestIdRef.current;
    const companyId = localStorage.getItem("email");
    if (!companyId) {
      if (requestId !== fetchRequestIdRef.current) return;
      setData([]);
      return;
    }

    const { dateRange, filters } = state;
    const selectedDate = dateRange?.from || getToday();
    const useTextFilters = !window.matchMedia("(max-width: 767px)").matches;

    let query = supabase
      .from("reservations")
      .select("*")
      .eq("company", companyId)
      .eq("date", selectedDate) // 운행일로 필터링
      .range(0, 4999); // 5,000개까지 (단일 날짜라서 보통 1,000개 넘지는 않을 듯)

    // 🔍 필터 조건
    if (useTextFilters && filters["사용자"]) {
      query = query.ilike("user", `%${filters["사용자"]}%`);
    }
    if (useTextFilters && filters["출발지"]) {
      query = query.ilike("start", `%${filters["출발지"]}%`);
    }
    if (useTextFilters && filters["행선지"]) {
      query = query.ilike("destination", `%${filters["행선지"]}%`);
    }
    if (useTextFilters && filters["차량"]) {
      query = query.ilike("car_number", `%${filters["차량"]}%`);
    }
    if (useTextFilters && filters["기사"]) {
      query = query.ilike("human_name", `%${filters["기사"]}%`);
    }

    const { data: result, error } = await query;
    if (requestId !== fetchRequestIdRef.current) return;

    if (!error && result) {
      // 차량번호 > 예약유형 > 출발시간 순으로 정렬
      const typeOrder = { "일반": 0, "학단": 1, "관광": 2, "통근": 3 };
      const sorted = result.sort((a, b) => {
        const carA = a.car_number || "";
        const carB = b.car_number || "";
        if (carA < carB) return -1;
        if (carA > carB) return 1;

        const aOrder = typeOrder[a.reservation_type] ?? 99;
        const bOrder = typeOrder[b.reservation_type] ?? 99;
        if (aOrder !== bOrder) return aOrder - bOrder;

        return getTimeSortValue(a.time) - getTimeSortValue(b.time);
      });

      setData(sorted);
      setSelectedIds(new Set());
      setExtraMessages(
        sorted.reduce((acc, row) => {
          if (row.id && row.driver_sms_extra_message) {
            acc[row.id] = row.driver_sms_extra_message;
          }
          return acc;
        }, {})
      );
    } else {
      console.error("❌ Supabase fetch error:", error?.message);
      setData([]);
      setSelectedIds(new Set());
      setExtraMessages({});
    }
  };

  // ✅ filters / dateRange.from 이 바뀔 때마다 자동 조회
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.dateRange.from,
    state.filters.사용자,
    state.filters.출발지,
    state.filters.행선지,
    state.filters.차량,
    state.filters.기사,
  ]);

  const handleFilter = () => {
    // 여기서는 별도 applied 스냅샷 없이 바로 fetch
    fetchData();
  };

  const visibleIds = data.map((row) => row.id).filter(Boolean);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  const toggleSelected = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllSelected = () => {
    setSelectedIds((prev) => {
      if (visibleIds.length > 0 && visibleIds.every((id) => prev.has(id))) return new Set();
      return new Set(visibleIds);
    });
  };

  const openExtraMessageModal = (row) => {
    setExtraMessageModal({
      id: row.id,
      label: [row.date, row.human_name, row.user].filter(Boolean).join(" / "),
      value: extraMessages[row.id] || "",
    });
  };

  const closeExtraMessageModal = () => {
    setExtraMessageModal(null);
  };

  const saveExtraMessageModal = async () => {
    if (!extraMessageModal?.id || extraMessageSaving) return;

    const targetRow = data.find((row) => row.id === extraMessageModal.id);
    if (!targetRow) {
      showUserAlert("기타 메시지를 저장할 예약을 찾지 못했습니다.");
      return;
    }

    const nextExtraMessages = {
      ...extraMessages,
      [extraMessageModal.id]: extraMessageModal.value || "",
    };

    setExtraMessageSaving(true);
    try {
      const result = await saveDriverSmsExtraMessages([targetRow], nextExtraMessages);
      if (result.failedCount > 0) {
        showUserAlert(
          "기타 메시지를 저장하지 못했습니다." +
          (result.error?.message ? `\n오류: ${userErrorMessage(result.error)}` : "")
        );
        return;
      }

      setExtraMessages(nextExtraMessages);
      // 저장 전에 시작된 느린 조회가 저장값을 다시 덮지 못하게 한다.
      fetchRequestIdRef.current += 1;
      closeExtraMessageModal();
    } finally {
      setExtraMessageSaving(false);
    }
  };

  const setLocalDriverSmsStatuses = (reservationIds, status, errorMessage = "") => {
    if (!reservationIds.length || !status) return;
    setData((prev) =>
      prev.map((row) =>
        reservationIds.includes(row.id)
          ? {
              ...row,
              driver_sms_status: status,
              driver_sms_sent_at: status === "sent" ? new Date().toISOString() : row.driver_sms_sent_at,
              driver_sms_failed_at: status === "failed" ? new Date().toISOString() : row.driver_sms_failed_at,
              driver_sms_error: status === "failed" ? errorMessage : null,
            }
          : row
      )
    );
  };

  const saveReservationDriverSmsStatus = async (reservationIds, status, errorMessage = "") => {
    if (!reservationIds.length || !status) {
      return { savedIds: [], missingIds: [], error: null };
    }

    const nowIso = new Date().toISOString();
    const company = String(localStorage.getItem("email") || "").trim();
    const updatePayload =
      status === "sent"
        ? {
            driver_sms_status: "sent",
            driver_sms_sent_at: nowIso,
            driver_sms_failed_at: null,
            driver_sms_error: null,
          }
        : {
            driver_sms_status: "failed",
            driver_sms_failed_at: nowIso,
            driver_sms_error: errorMessage || null,
          };

    let updateQuery = supabase
      .from("reservations")
      .update(updatePayload)
      .in("id", reservationIds)
      .select("id");

    if (company) updateQuery = updateQuery.eq("company", company);

    const { error: updateError } = await updateQuery;
    if (updateError) console.warn("기사 문자 상태 저장 실패:", updateError);

    let verifyQuery = supabase
      .from("reservations")
      .select("id, driver_sms_status")
      .in("id", reservationIds);

    if (company) verifyQuery = verifyQuery.eq("company", company);

    const { data: verifiedRows, error: verifyError } = await verifyQuery;
    const savedIds = (verifiedRows || [])
      .filter((row) => row.driver_sms_status === status)
      .map((row) => row.id);
    const savedIdSet = new Set(savedIds);
    const missingIds = reservationIds.filter((id) => !savedIdSet.has(id));

    if (verifyError || missingIds.length > 0) {
      console.warn("기사 문자 상태 DB 확인 실패:", {
        error: verifyError || updateError || null,
        status,
        missingIds,
      });
    }

    setLocalDriverSmsStatuses(savedIds, status, errorMessage);
    return {
      savedIds,
      missingIds,
      error: verifyError || updateError || null,
    };
  };

  const saveDriverSmsExtraMessages = async (rows, messageValues = extraMessages) => {
    const company = localStorage.getItem("email");
    const targets = rows
      .map((row) => ({
        id: row.id,
        message: String(messageValues[row.id] || "").trim(),
      }))
      .filter(({ id }) => id);

    if (!targets.length) return { savedCount: 0, failedCount: 0, error: null };

    const results = await Promise.all(
      targets.map(async ({ id, message }) => {
        let query = supabase
          .from("reservations")
          .update({ driver_sms_extra_message: message || null })
          .eq("id", id)
          .select("id, driver_sms_extra_message");

        if (company) query = query.eq("company", company);

        const result = await query;
        return {
          id,
          message,
          error: result.error,
          saved:
            !result.error &&
            (result.data || []).some(
              (item) =>
                item.id === id &&
                String(item.driver_sms_extra_message || "").trim() === message
            ),
        };
      })
    );

    const savedResults = results.filter((result) => result.saved);
    const failedResults = results.filter((result) => !result.saved);
    const error = failedResults.find((result) => result.error)?.error || null;
    if (failedResults.length) console.warn("기사 문자 기타 메시지 저장 실패:", error || failedResults);

    setData((prev) =>
      prev.map((row) => {
        const target = savedResults.find((item) => item.id === row.id);
        return target
          ? { ...row, driver_sms_extra_message: target.message || null }
          : row;
      })
    );
    return {
      savedCount: savedResults.length,
      failedCount: failedResults.length,
      error,
    };
  };

  const buildDriverSmsMessage = (row) => {
    // 배차처리의 reservations.memo는 기사 SMS에 자동 포함하지 않는다.
    const manualExtraMessage = String(extraMessages[row.id] || "").trim();
    const userPhone = String(row.user_phone || "").trim();
    return [
      buildScheduleSmsMessage(row, { includeTripType: true }),
      manualExtraMessage ? `기타: ${manualExtraMessage}` : null,
      userPhone ? `사용자 전화번호: ${userPhone}` : null,
    ].filter(Boolean).join("\n\n");
  };

  const sendBulkSms = async (target) => {
    if (smsSending) return;

    const selectedRows = data.filter((row) => selectedIds.has(row.id));
    if (selectedRows.length === 0) {
      showUserAlert("문자를 보낼 행을 선택해 주세요.");
      return;
    }

    const { company, companyName } = await resolveCurrentCompanyForSms();
    if (!company) {
      showUserAlert("회사 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
      return;
    }

    const { data: smsSettings, error: senderError } = await supabase.functions.invoke("sms-settings", {
      body: { action: "get" },
    });

    if (senderError) {
      const message = await readFunctionErrorMessage(senderError);
      showUserAlert(message || "SMS 문자 발신 번호를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.");
      return;
    }

    if (
      !String(smsSettings?.phone_number || "").trim() ||
      !smsSettings?.api_key_registered ||
      !smsSettings?.api_secret_registered
    ) {
      showUserAlert("'SMS 문자 발신 번호'화면에서 발신번호와 Solapi API Key 및 Secret을 등록해주세요. 현재 당사는 Solapi 문자 서비스를 이용하고 있습니다.");
      return;
    }

    const rowsWithPhone = selectedRows
      .map((row) => {
        const toPhone = target === "driver" ? row.phone : row.user_phone;
        const message = target === "driver"
          ? buildDriverSmsMessage(row)
          : buildUserSmsMessage(row, extraMessages[row.id]);
        return {
          row,
          toPhone: String(toPhone || "").trim(),
          message,
        };
      })
      .filter((item) => item.toPhone);

    if (rowsWithPhone.length === 0) {
      showUserAlert(target === "driver" ? "선택한 행에 기사 연락처가 없습니다." : "선택한 행에 사용자 전화번호가 없습니다.");
      return;
    }

    const missingCount = selectedRows.length - rowsWithPhone.length;
    const targetLabel = target === "driver" ? "기사" : "사용자";
    if (!window.confirm(`${targetLabel} 문자 ${rowsWithPhone.length}건을 발송 대기열에 등록할까요?${missingCount ? `\n연락처가 없는 ${missingCount}건은 제외됩니다.` : ""}`)) {
      return;
    }

    setSmsSending(true);
    let smsStage = "발송 준비";
    try {
      const nowIso = new Date().toISOString();
      const payload = rowsWithPhone.map(({ row, toPhone, message }) => ({
        reservation_id: row.id,
        company,
        company_name: row.company_name || companyName || null,
        to_phone: toPhone,
        message,
        send_at: nowIso,
        status: "pending",
        target_type: target,
      }));

      smsStage = "sms_outbox 등록";
      const { data: insertedRows, error } = await supabase
        .from("sms_outbox")
        .insert(payload)
        .select("id,reservation_id");
      if (error) throw error;

      const insertedIds = (insertedRows || []).map((row) => row.id).filter(Boolean);
      if (insertedIds.length === 0) {
        throw new Error("발송 대기열 id를 확인하지 못했습니다.");
      }

      if (target === "driver") {
        const extraMessageResult = await saveDriverSmsExtraMessages(
          rowsWithPhone.map(({ row }) => row)
        );
        if (extraMessageResult.failedCount > 0) {
          throw new Error("기사 문자 기타 메세지를 저장하지 못했습니다.");
        }
      }

      const sentOutboxIds = new Set();
      const failedOutbox = [];
      for (let offset = 0; offset < insertedIds.length; offset += 20) {
        const batch = insertedIds.slice(offset, offset + 20);
        smsStage = "문자 발송 처리";
        const { data: result, error: processError } = await supabase.functions.invoke("process-sms-outbox", {
          body: { batchSize: batch.length, ids: batch },
        });
        if (processError) {
          const response = await processError.context?.clone?.().json().catch(() => null);
          failedOutbox.push({ error: response?.error || processError.message });
          break;
        }
        (result?.sentIds || []).forEach((id) => sentOutboxIds.add(Number(id)));
        failedOutbox.push(...(result?.failed || []));
      }
      const failedIds = new Set(failedOutbox.map((item) => Number(item.id)).filter(Boolean));
      let statusSaveFailureCount = 0;
      if (target === "driver") {
        const sentReservations = insertedRows.filter((row) => sentOutboxIds.has(Number(row.id))).map((row) => row.reservation_id);
        const failedReservations = insertedRows.filter((row) => failedIds.has(Number(row.id))).map((row) => row.reservation_id);
        if (sentReservations.length) {
          const result = await saveReservationDriverSmsStatus(sentReservations, "sent");
          statusSaveFailureCount += result.missingIds.length;
        }
        if (failedReservations.length) {
          const result = await saveReservationDriverSmsStatus(
            failedReservations,
            "failed",
            failedOutbox[0]?.error || ""
          );
          statusSaveFailureCount += result.missingIds.length;
        }
      }
      const unresolved = insertedIds.length - sentOutboxIds.size - failedIds.size;
      const firstError = failedOutbox[0]?.error;
      showUserAlert(`${targetLabel} 문자 ${sentOutboxIds.size}건을 발송했고, ${failedIds.size}건은 실패했습니다.` +
        (unresolved ? `\n${unresolved}건은 대기 중이거나 발송 결과 확인이 필요합니다. 재발송 전에 Solapi 내역을 확인해 주세요.` : "") +
        (statusSaveFailureCount ? `\n${statusSaveFailureCount}건은 문자 결과를 예약 DB에 저장하지 못했습니다.` : "") +
        (firstError ? `\n${getUserFriendlySmsError(firstError) || userErrorMessage(firstError)}` : ""));

    } catch (error) {
      showUserAlert(`${targetLabel} 문자 발송 실패\n단계: ${smsStage}\n오류: ${userErrorMessage(error)}`);
    } finally {
      setSmsSending(false);
    }
  };

  return (
    <div className="space-y-1">
      <style>{`
        .confirmed-schedule-table th,
        .confirmed-schedule-table td {
          padding-top: 0.125rem !important;
          padding-bottom: 0.125rem !important;
          line-height: 1.15 !important;
          text-align: center;
        }

        .confirmed-schedule-table .col-date {
          width: 96px;
          min-width: 96px;
          white-space: nowrap;
        }

        .confirmed-schedule-table .col-type {
          width: 52px;
          min-width: 52px;
          white-space: nowrap;
        }

        .confirmed-schedule-table .col-car {
          width: 86px;
          min-width: 86px;
          white-space: nowrap;
        }

        .confirmed-schedule-table .col-driver {
          width: 56px;
          min-width: 56px;
          white-space: nowrap;
          word-break: keep-all;
        }

        .confirmed-schedule-table .col-location {
          width: 160px;
          min-width: 160px;
          text-align: left;
        }

        .confirmed-schedule-table .col-extra-message {
          width: 160px;
          min-width: 160px;
          max-width: 160px;
          text-align: left;
        }

        .confirmed-schedule-table .extra-message-preview {
          display: block;
          width: 100%;
          max-width: 100%;
          min-height: 1.75rem;
          height: 1.75rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          border: 1px solid #d1d5db;
          border-radius: 0.25rem;
          background: #fff;
          padding: 0.25rem 0.5rem;
          text-align: left;
          font-size: 0.875rem;
          line-height: 1.25rem;
        }

        .confirmed-schedule-table .extra-message-preview.empty {
          color: #9ca3af;
        }
      `}</style>
      <Header />
      <div className="px-4 pt-1 pb-4 space-y-4">
        <h2 className="text-lg font-semibold">배차 일정표</h2>

        {/* 🔁 검색 조건을 usePersistedUI와 묶음 */}
        <div className="flex flex-wrap gap-2 items-end">
          <Input
            className="hidden md:block w-40"
            placeholder="사용자"
            {...ui.bind("filters.사용자")}
          />
          <Input
            className="hidden md:block w-40"
            placeholder="출발지"
            {...ui.bind("filters.출발지")}
          />
          <Input
            className="hidden md:block w-40"
            placeholder="행선지"
            {...ui.bind("filters.행선지")}
          />
          <Input
            className="hidden md:block w-40"
            placeholder="차량"
            {...ui.bind("filters.차량")}
          />
          {/* 기존 '기사명' → 공용 훅과 맞춰 '기사'로 통일 */}
          <Input
            className="hidden md:block w-40"
            placeholder="기사명"
            {...ui.bind("filters.기사")}
          />

          <span className="text-sm">운행일</span>
          <Input
            type="date"
            {...ui.bind("dateRange.from")}
          />

          <Button onClick={handleFilter}>검색</Button>
        </div>

        <Card>
          <CardContent className="bg-white">
            <div className="max-h-[70vh] overflow-auto">
              <table className="confirmed-schedule-table table-auto w-full border border-gray-300 text-sm">
                <thead className="sticky top-0 z-30 bg-blue-500 text-white">
                  <tr>
                    <th className="sticky top-0 z-30 border border-gray-300 bg-blue-500 p-2 w-10 hidden md:table-cell">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAllSelected}
                        aria-label="전체 선택"
                      />
                    </th>
                    {[
                      "순번",
                      "운행일",
                      "구분",
                      "차량번호",
                      "기사명",
                      "핸드폰",
                      "시간",
                      "출발지",
                      "경유지",
                      "행선지",
                      "기타 메세지",
                      "편도/왕복",
                      "사용자",
                      "차량료",
                    ].map((header) => (
                      <th
                        key={header}
                        className={
                          "sticky top-0 z-30 border border-gray-300 bg-blue-500 p-2 " +
                          (header === "운행일" ? "col-date " : "") +
                          (header === "구분" ? "col-type " : "") +
                          (header === "차량번호" ? "col-car " : "") +
                          (header === "기사명" ? "col-driver " : "") +
                          (["출발지", "경유지", "행선지"].includes(header) ? "col-location " : "") +
                          (header === "기타 메세지" ? "col-extra-message " : "") +
                          (["차량번호", "기사명", "시간", "출발지", "기타 메세지", "사용자"].includes(header) ? "mobile-schedule-cell" : "hidden md:table-cell")
                        }
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, index) => {
                    const isSelected = selectedIds.has(row.id);
                    const driverSmsStatus = row.driver_sms_status;
                    const rowColorClass =
                      driverSmsStatus === "sent"
                        ? "bg-green-100"
                        : driverSmsStatus === "failed"
                        ? "bg-red-200"
                        : isSelected
                        ? "bg-red-50"
                        : "";

                    return (
                    <tr
                      key={row.id}
                      className={rowColorClass}
                      aria-selected={isSelected}
                      aria-label={`${row.car_number || "차량번호 없음"} ${row.human_name || "기사명 없음"} 문자 발송 선택`}
                      tabIndex={0}
                      onClick={(event) => {
                        if (window.matchMedia("(max-width: 767px)").matches && !event.target.closest("button")) {
                          toggleSelected(row.id);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (window.matchMedia("(max-width: 767px)").matches && ["Enter", " "].includes(event.key) && event.target === event.currentTarget) {
                          event.preventDefault();
                          toggleSelected(row.id);
                        }
                      }}
                    >
                      <td className="border p-2 hidden md:table-cell">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelected(row.id)}
                          aria-label={`${index + 1}번 선택`}
                        />
                      </td>
                      <td className="border p-2 hidden md:table-cell">{index + 1}</td>
                      <td className="border p-2 col-date hidden md:table-cell">{row.date}</td>
                      <td className="border p-2 col-type hidden md:table-cell">{row.reservation_type}</td>
                      <td className="border p-2 col-car mobile-schedule-cell" data-label="차량번호">{row.car_number}</td>
                      <td className="border p-2 col-driver mobile-schedule-cell" data-label="기사명">{row.human_name}</td>
                      <td className="border p-2 hidden md:table-cell">{row.phone}</td>
                      <td className="border p-2 mobile-schedule-cell" data-label="시간">
                        {row.time
                          ? new Date(`1970-01-01T${row.time}`).toLocaleTimeString("ko-KR", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })
                          : ""}
                      </td>
                      <td className="border p-2 col-location mobile-schedule-cell" data-label="출발지">{row.start}</td>
                      <td className="border p-2 col-location hidden md:table-cell">{row.transit}</td>
                      <td className="border p-2 col-location hidden md:table-cell">{row.destination}</td>
                      <td className="border p-1 col-extra-message mobile-schedule-cell" data-label="기타 메세지">
                        <button
                          type="button"
                          className={
                            "extra-message-preview " +
                            (String(extraMessages[row.id] || "").trim() ? "" : "empty")
                          }
                          onClick={() => openExtraMessageModal(row)}
                          title={extraMessages[row.id] || "기타 메세지 입력"}
                        >
                          {extraMessages[row.id] || "클릭해서 입력"}
                        </button>
                      </td>
                      <td className="border p-2 hidden md:table-cell">{row.one_round}</td>
                      <td className="border p-2 mobile-schedule-cell" data-label="사용자">{row.user}</td>
                      <td className="border p-2 hidden md:table-cell">{row.money}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        <div className="flex justify-end gap-2">
          <Button
            onClick={() => sendBulkSms("driver")}
            disabled={smsSending || selectedIds.size === 0}
            className={smsSending || selectedIds.size === 0 ? "cursor-not-allowed opacity-50" : ""}
          >
            {smsSending ? "처리 중..." : "기사 문자 보내기"}
          </Button>
          <Button
            onClick={() => sendBulkSms("user")}
            disabled={smsSending || selectedIds.size === 0}
            className={`hidden md:inline-flex ${smsSending || selectedIds.size === 0 ? "cursor-not-allowed opacity-50" : ""}`}
          >
            사용자 문자 보내기
          </Button>
        </div>
      </div>
      {extraMessageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded bg-white p-4 shadow-lg">
            <div className="mb-3">
              <h3 className="text-base font-semibold">기타 메세지 입력</h3>
              {extraMessageModal.label && (
                <p className="mt-1 text-sm text-gray-500">{extraMessageModal.label}</p>
              )}
            </div>
            <textarea
              value={extraMessageModal.value}
              onChange={(e) =>
                setExtraMessageModal((prev) => (
                  prev ? { ...prev, value: e.target.value } : prev
                ))
              }
              className="h-32 w-full resize-y rounded border border-gray-300 p-2 text-sm outline-none focus:border-blue-500"
              placeholder="기사 문자에 추가로 보낼 기타 메세지를 입력하세요."
              autoFocus
              disabled={extraMessageSaving}
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeExtraMessageModal}
                disabled={extraMessageSaving}
              >
                취소
              </Button>
              <Button type="button" onClick={saveExtraMessageModal} disabled={extraMessageSaving}>
                {extraMessageSaving ? "저장 중..." : "입력"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
